import { appendParameters, axiosAuth, axiosBilling, axiosPlatform } from "../../_services/axios-helper";

export const adminAreaService = {
    getAllOrganizations,
    getAllSelectableOrganizations,
    getOrganizationNames,
    getReducedOrganizationByExternalUUID,
    getOrganization,
    deleteOrganization,
    updateOrganization,
    getAllUsers,
    getUserByFilter,
    getUser,
    deleteUser,
    updateUser,
    revokeLastPasswordChange,
    getAllClients,
    getClient,
    createNewResetPasswordLink,
    getOrganizationsWithBillingEnabled,
    reprocessOrderData,
    recreateAllDocuments,
    triggerDownloadForAllDocuments,
    importOrder,

    validateOrRefreshShopifyWebhooks,
    configurationCatchupStart,
    setInTestMode,
    triggerHealthReport,
    loadSimpleOrganizations,

    getAllApplications,
    getAppStoreOrganizations,
    chargeOrganizations,
    resetToRegularOrganization,

    getChargeBeeAutoSetupMessages,

    getAmazonPayoutFees,
    createAmazonPayoutFee,
    editAmazonPayoutFee,
    deleteAmazonPayoutFee,
    getAmazonPayoutFeeUsage,

    getThreadDump,
    stopThread,

    getUserLoginStatistics,
    saveGlobalSettings,
    getGlobalSettings,
    getAuditChanges,
    getOrganizationAndUsersAuditChanges,
    getParallelTestsData,
    getParallelTestsDataForOrganization,
    executeParallelTestForClientAndIntegration,

    triggerHubspotOrganizationSync,
    triggerHubspotContactSync,
    createBugReport,
    getActiveMilestones,
    getProjectLabels,
};

function getAllOrganizations(filterProperties) {
    return axiosPlatform().post("/admin/organizations", filterProperties);
}

function getAllSelectableOrganizations() {
    return axiosAuth().get("/platform/selectable-organizations");
}

function getOrganizationNames() {
    return axiosAuth().get("/platform/selectable-organizations/names");
}

function getReducedOrganizationByExternalUUID(organizationUUID) {
    return axiosAuth().get("/platform/reduced-organization/" + organizationUUID);
}

function getUserByFilter(userFilterProperties) {
    return axiosPlatform().post("/admin/users", userFilterProperties);
}

function getOrganization(uuid) {
    return axiosPlatform().get("/admin/organization?id=" + uuid);
}

function getOrganizationsWithBillingEnabled(filterProperties) {
    return axiosPlatform().post("/admin/billing/invoicingconfigurations", filterProperties);
}

function deleteOrganization(uuid, ignorePermissionsAndNotifications = false) {
    return axiosPlatform().delete(`/admin/organization/${uuid}`, { params: { ignorePermissionsAndNotifications } });
}

function updateOrganization(organization) {
    return axiosPlatform().post("/admin/organization", organization);
}

function getAllUsers() {
    return axiosPlatform().get("/admin/users");
}

function getUser(uuid) {
    return axiosPlatform().get("/admin/user?id=" + uuid);
}

function deleteUser(uuid) {
    return axiosPlatform().delete("/admin/user/" + uuid);
}

function updateUser(user) {
    return axiosPlatform().post("/admin/user", user);
}

function revokeLastPasswordChange(userUUID) {
    return axiosPlatform().post("/admin/user/" + userUUID + "/revoke-password-reset", {});
}

function createNewResetPasswordLink(userUUID) {
    return axiosPlatform().post("/admin/user/setupresetpassword/" + userUUID);
}

function getAllClients() {
    return axiosPlatform().get("/admin/client");
}

function getClient(uuid) {
    return axiosPlatform().get("/admin/client?uuid" + uuid);
}

function getAppStoreOrganizations(filterProperties) {
    return axiosPlatform().post("/admin/organizations/appstore", filterProperties);
}

function getAllApplications() {
    return axiosPlatform().get("/admin/applications");
}

function chargeOrganizations(request) {
    return axiosAuth().post("/platform/billing/charge", request);
}

function getChargeBeeAutoSetupMessages(configurationUUID) {
    return axiosPlatform().get("/integrations/chargebee/autosetupmessages/" + configurationUUID);
}

function getAmazonPayoutFees() {
    return axiosPlatform().get("/admin/amazonpayoutfees");
}

function createAmazonPayoutFee(request) {
    return axiosPlatform().post("/admin/amazonpayoutfees", request);
}

function editAmazonPayoutFee(request) {
    return axiosPlatform().post("/admin/amazonpayoutfees/update", request);
}

function deleteAmazonPayoutFee(externalUUID) {
    return axiosPlatform().delete("/admin/amazonpayoutfees/" + externalUUID);
}

function getAmazonPayoutFeeUsage(externalUUID) {
    return axiosPlatform().get("/admin/amazonpayoutfees/" + externalUUID + "/usage");
}

function getThreadDump() {
    return axiosPlatform().get("/admin/threaddump");
}

function stopThread(request) {
    return axiosPlatform().post("/admin/threaddump", request);
}

function getUserLoginStatistics() {
    return axiosPlatform().get("/admin/userstatistics");
}

function validateOrRefreshShopifyWebhooks(configurationId) {
    return axiosBilling().post("/billing/invoiceconfiguration/" + configurationId + "/shopify-webhooks");
}

function configurationCatchupStart(configurationId) {
    return axiosBilling().post("/billing/invoiceconfiguration/" + configurationId + "/catchup");
}

function reprocessOrderData(configurationId) {
    return axiosBilling().put("/billing/orders/configuration/" + configurationId + "/reprocess-orderdata");
}

function recreateAllDocuments(configurationId) {
    return axiosBilling().put("/billing/orders/configuration/" + configurationId + "/regenerate-documents");
}

function triggerDownloadForAllDocuments(configurationId) {
    return axiosBilling().post("/billing/downloads/full", { externalUUID: configurationId });
}

function importOrder(shopDomain, orderName, configurationId) {
    return axiosBilling().get(appendParameters("/billing/orders/" + configurationId + "/manual-import", { orderName }));
}

function setInTestMode(configurationId) {
    return axiosBilling().put("/billing/invoiceconfiguration/" + configurationId + "/set-in-test-mode");
}

function triggerHealthReport(configurationId) {
    return axiosBilling().post("/billing/invoiceconfiguration/" + configurationId + "/health-report");
}

function loadSimpleOrganizations() {
    return axiosPlatform().get("/admin/billing/organizations");
}

function resetToRegularOrganization(externalUUID) {
    return axiosPlatform().post("/admin/organizations/set-appstore-organization", { externalUUID });
}

function saveGlobalSettings(globalSettings) {
    return axiosPlatform().post("/globalsettings", globalSettings);
}

function getGlobalSettings() {
    return axiosPlatform().get("/globalsettings");
}

function getAuditChanges(organizationExternalUUID, lookBackDays) {
    return axiosPlatform().get("/audit/" + organizationExternalUUID + "/changes?lookBackDays=" + lookBackDays);
}

function getOrganizationAndUsersAuditChanges(organizationUUID, lookBackDays) {
    return axiosAuth().get(
        `/platform/audit/organization?organizationExternalUUID=${organizationUUID}&lookBackDays=${lookBackDays}`
    );
}

function getParallelTestsData() {
    return axiosPlatform().get(`/parallel-tests`);
}

function getParallelTestsDataForOrganization(organizationExternalUUID) {
    return axiosPlatform().get(`/parallel-tests/${organizationExternalUUID}`);
}

function executeParallelTestForClientAndIntegration(request) {
    return axiosPlatform().post(`/parallel-tests/integration/execute`, request);
}

function triggerHubspotOrganizationSync(organizationUUID) {
    return axiosAuth().get("/hubspot/sync-organization/" + organizationUUID);
}

function triggerHubspotContactSync(userUUID) {
    return axiosAuth().get("/hubspot/sync-contact/" + userUUID);
}

function createBugReport(formData) {
    return axiosPlatform().post("/admin/bugs/create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
    });
}

function getActiveMilestones() {
    return axiosPlatform().get("/admin/bugs/milestones");
}

function getProjectLabels(forceRefresh = false) {
    return axiosPlatform().get("/admin/bugs/labels", forceRefresh ? { params: { forceRefresh: true } } : undefined);
}
